import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Grid,
  Divider,
  Chip,
  InputAdornment,
  Alert
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import TwitterIcon from '@mui/icons-material/Twitter';
import LanguageIcon from '@mui/icons-material/Language';
import YouTubeIcon from '@mui/icons-material/YouTube';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import LinkIcon from '@mui/icons-material/Link';
import XIcon from '@mui/icons-material/X';

const SOCIAL_MEDIA_PLATFORMS = {
  website: { name: 'Website', icon: <LanguageIcon />, prefix: '' },
  facebook: { name: 'Facebook', icon: <FacebookIcon color="info" />, prefix: 'https://facebook.com/' },
  instagram: { name: 'Instagram', icon: <InstagramIcon color="error" />, prefix: 'https://instagram.com/' },
  twitter: { name: 'X', icon: <XIcon color="black" />, prefix: 'https://twitter.com/' },
  youtube: { name: 'YouTube', icon: <YouTubeIcon color="error" />, prefix: 'https://youtube.com/' },
  linkedin: { name: 'LinkedIn', icon: <LinkedInIcon color="info" />, prefix: 'https://linkedin.com/company/' },
  other: { name: 'Other', icon: <LinkIcon />, prefix: '' }
};

function SocialMediaEditor({ socialMediaLinks = {}, onChange }) {
  const [newPlatform, setNewPlatform] = useState('');
  const [newUrl, setNewUrl] = useState('');
  const [error, setError] = useState('');

  const handleAddLink = () => {
    // Validate input
    if (!newPlatform) {
      setError('Please select a platform');
      return;
    }
    
    if (!newUrl.trim()) {
      setError('Please enter a URL or username');
      return;
    }
    
    // Format URL based on platform
    let finalUrl = newUrl.trim();
    const platform = SOCIAL_MEDIA_PLATFORMS[newPlatform];
    
    // For platforms with prefixes, handle both full URLs and usernames
    if (platform.prefix && !finalUrl.startsWith('http')) {
      // Remove @ if present (common in social media handles)
      if (finalUrl.startsWith('@')) {
        finalUrl = finalUrl.substring(1);
      }
      
      // Remove platform prefix if user already included it
      if (finalUrl.includes(platform.prefix.replace('https://', ''))) {
        finalUrl = finalUrl.replace(platform.prefix.replace('https://', ''), '');
      }
      
      // Add the full prefix
      finalUrl = platform.prefix + finalUrl;
    }
    
    // For websites, ensure it has http/https
    if (newPlatform === 'website' && !finalUrl.startsWith('http')) {
      finalUrl = 'https://' + finalUrl;
    }
    
    // Update the social media links
    const updatedLinks = {
      ...socialMediaLinks,
      [newPlatform]: finalUrl
    };
    
    // Call the onChange callback
    onChange(updatedLinks);
    
    // Reset form
    setNewPlatform('');
    setNewUrl('');
    setError('');
  };

  const handleRemoveLink = (platform) => {
    const updatedLinks = { ...socialMediaLinks };
    delete updatedLinks[platform];
    onChange(updatedLinks);
  };

  // Get platforms that haven't been added yet
  const availablePlatforms = Object.keys(SOCIAL_MEDIA_PLATFORMS).filter(
    platform => !socialMediaLinks[platform]
  );

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Social Media Accounts
      </Typography>
      
      {/* Display existing links */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {Object.keys(socialMediaLinks).length > 0 ? (
          Object.entries(socialMediaLinks).map(([platform, url]) => (
            <Grid item xs={12} key={platform}>
              <Box 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  p: 2, 
                  border: '1px solid', 
                  borderColor: 'divider',
                  borderRadius: 1
                }}
              >
                <Box sx={{ mr: 2 }}>
                  {SOCIAL_MEDIA_PLATFORMS[platform]?.icon || <LinkIcon />}
                </Box>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="subtitle2">
                    {SOCIAL_MEDIA_PLATFORMS[platform]?.name || 'Custom Link'}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    component="a" 
                    href={url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    sx={{ color: 'text.secondary', wordBreak: 'break-all' }}
                  >
                    {url}
                  </Typography>
                </Box>
                <IconButton 
                  color="error" 
                  onClick={() => handleRemoveLink(platform)}
                  size="small"
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            </Grid>
          ))
        ) : (
          <Grid item xs={12}>
            <Typography color="text.secondary" align="center" sx={{ py: 2 }}>
              No social media accounts added yet.
            </Typography>
          </Grid>
        )}
      </Grid>
      
      <Divider sx={{ mb: 3 }} />
      
      {/* Add new link form */}
      <Typography variant="subtitle1" gutterBottom>
        Add Social Media Account
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} md={4}>
          <Box>
            {availablePlatforms.length > 0 ? (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {availablePlatforms.map(platform => (
                  <Chip
                    key={platform}
                    icon={SOCIAL_MEDIA_PLATFORMS[platform].icon}
                    label={SOCIAL_MEDIA_PLATFORMS[platform].name}
                    onClick={() => setNewPlatform(platform)}
                    variant={newPlatform === platform ? 'filled' : 'outlined'}
                    color={newPlatform === platform ? 'primary' : 'default'}
                    sx={{ mb: 1 }}
                  />
                ))}
              </Box>
            ) : (
              <Typography color="text.secondary">
                All platforms added! Remove one to add a different account.
              </Typography>
            )}
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label={newPlatform ? `${SOCIAL_MEDIA_PLATFORMS[newPlatform].name} URL or Username` : "Select a platform first"}
            value={newUrl}
            onChange={(e) => setNewUrl(e.target.value)}
            disabled={!newPlatform}
            helperText={
              newPlatform && SOCIAL_MEDIA_PLATFORMS[newPlatform].prefix 
                ? `You can enter a full URL or just the username (${SOCIAL_MEDIA_PLATFORMS[newPlatform].prefix}username)`
                : ''
            }
            InputProps={{
              startAdornment: newPlatform && (
                <InputAdornment position="start">
                  {SOCIAL_MEDIA_PLATFORMS[newPlatform].icon}
                </InputAdornment>
              )
            }}
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <Button
            fullWidth
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddLink}
            disabled={!newPlatform || !newUrl.trim()}
          >
            Add
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
}

export default SocialMediaEditor;